import React, { useState } from 'react';
import axios from 'axios';

const API_URL = "https://holisticphysiotherapy.in/holistic_bill/api.php"; 

export default function PatientRegistration() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Form State
  const initialFormState = {
    id: '',
    uhid: '',
    name: '',
    age: '',
    gender: 'Male',
    phone: '',
    address: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);

  // Search Patient by UHID, Mobile Number, or Name
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setLoadingSearch(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await axios.get(`${API_URL}?action=search_patient&query=${encodeURIComponent(searchTerm)}`);
      if (res.data.status === 'success') {
        setSearchResults(res.data.patients || []);
        if (res.data.patients.length === 0) {
          setMessage({ type: 'warning', text: 'No patient found matching your search term.' });
        }
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Error fetching patient details.' });
    } finally {
      setLoadingSearch(false);
    }
  };

  // Select patient from search results to view/edit
  const handleSelectPatient = (patient) => {
    setFormData({
      id: patient.id,
      uhid: patient.uhid,
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      phone: patient.phone,
      address: patient.address || ''
    });
    setIsEditing(true);
    setSearchResults([]);
    setSearchTerm('');
  };

  // Clear form & search results for a brand new patient registration
  const handleNewPatient = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setSearchResults([]);
    setSearchTerm('');
    setMessage({ type: 'info', text: 'Register a new patient.' });
  };

  // Input Change Handler
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Register or Update Patient
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    const action = isEditing ? 'update_patient' : 'register_patient';

    try {
      const res = await axios.post(`${API_URL}?action=${action}`, formData);
      if (res.data.status === 'success') {
        setMessage({ 
          type: 'success', 
          text: isEditing ? 'Patient details updated successfully!' : `Patient registered! UHID: ${res.data.uhid}` 
        });
        if (!isEditing) {
          setFormData({ ...formData, uhid: res.data.uhid });
        }
      } else {
        setMessage({ type: 'danger', text: res.data.message || 'Action failed.' });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: 'Server error while saving patient.' });
    }
  };

  return (
    <div className="container-fluid px-4 py-3">
      {/* Search Header */}
      <div className="card border-0 shadow-sm p-3 mb-4 bg-white rounded">
        <div className="row align-items-center">
          <div className="col-md-8">
            <form onSubmit={handleSearch} className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search patient by UHID, Mobile Number, or Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="btn px-4 fw-bold"  style={{backgroundColor:"#007C9D", color:"white"}} disabled={loadingSearch}>
                {loadingSearch ? 'Searching...' : '🔍 Search'}
              </button>
            </form>
          </div>
          <div className="col-md-4 text-md-end mt-2 mt-md-0">
            <button type="button" onClick={handleNewPatient} className="btn btn-outline-success fw-bold"  style={{color:"#007C9D"}}>
              ➕ New Patient
            </button>
          </div>
        </div>

        {/* Search Results Dropdown List */}
        {searchResults.length > 0 && (
          <div className="table-responsive mt-3 border rounded">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>UHID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Age / Gender</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((pt) => (
                  <tr key={pt.id}>
                    <td className="fw-bold">{pt.uhid}</td>
                    <td>{pt.name}</td>
                    <td>{pt.phone}</td>
                    <td>{pt.age} Yrs / {pt.gender}</td>
                    <td>
                      <button 
                        className="btn btn-sm"
                        style={{backgroundColor:"#007C9D", color:"white"}}
                        onClick={() => handleSelectPatient(pt)}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alert Message */}
      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
        </div>
      )}

      <div className="card border-0 shadow-sm p-4 bg-white rounded">
        <h5 className="fw-bold mb-3"  style={{color:"#007C9D"}}>
          {isEditing ? `Editing Patient Details (UHID: ${formData.uhid})` : 'New Patient Registration'}
        </h5>

        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-bold small">Full Name *</label>
              <input
                type="text"
                name="name"
                className="form-control"
                placeholder="Enter patient full name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-bold small">Mobile Phone Number *</label>
              <input
                type="tel"
                name="phone"
                className="form-control"
                placeholder="10-digit mobile number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold small">Age *</label>
              <input
                type="number"
                name="age"
                className="form-control"
                placeholder="Age in years"
                value={formData.age}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold small">Gender *</label>
              <select
                name="gender"
                className="form-select"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-bold small">UHID (Auto Generated)</label>
              <input
                type="text"
                className="form-control bg-light"
                value={formData.uhid || 'Generated on save'}
                disabled
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-bold small">Address</label>
              <textarea
                name="address"
                className="form-control"
                rows="2"
                placeholder="Patient locality/address"
                value={formData.address}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="col-12 text-end mt-4">
              <button type="submit" className="btn btn-primary px-4 fw-bold"  style={{backgroundColor:"#007C9D", color:"white"}}>
                {isEditing ? 'Update Patient' : 'Save & Register Patient'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}